import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import { useAllRestaurants } from '../../apis/shops-api';
import LoadingIndicator from '../../shared/LoadingIndicator';
import { PageContainer } from '../../shared/Styled/Styled';
import { RestaurantPageHeader } from '../../shared/RestaurantPageHeader/RestaurantPageHeader';
import { RestaurantOrdersByDay } from '../../shared/Orders/RestaurantOrders/RestaurantOrdersByDay';
import useRestaurantStore from '../../stores/restaurantStore';

type Params = {
  restaurant: string;
};

/** `/:restaurant/orders/`: one restaurant's orders, a day at a time. */
export const RestaurantOrdersPage: React.FC = () => {
  const { t } = useTranslation();
  const { selectedShopExternalId } = useRestaurantStore();
  const { restaurant: paramsRestaurantId } = useParams<Params>();
  const restaurantId = paramsRestaurantId || selectedShopExternalId;

  const { data: restaurants, isLoading } = useAllRestaurants();

  const restaurant = restaurants?.find((r) => r.shopExternalId === restaurantId);

  if (isLoading) {
    return <LoadingIndicator />;
  }

  if (!restaurant) {
    return null;
  }

  return (
    <PageContainer>
      {/* One shell child, so this page sets its own heading-to-content distance rather than taking
          the shell's — which is shared with pages whose blocks do want space between them. */}
      <Content>
        <RestaurantPageHeader title={t('nav.restaurant.orders')} restaurant={restaurant} />
        <RestaurantOrdersByDay shopExternalId={restaurant.shopExternalId} />
      </Content>
    </PageContainer>
  );
};

/**
 * On a phone the day strip follows the meta line directly — its own 12px of top padding is all the
 * separation the two need, and vertical room is scarce. From sm up they get the same distance the
 * other pages put between their blocks.
 */
const Content = styled.div`
  display: flex;
  flex-direction: column;

  ${({ theme }) => theme.breakpoints.up('sm')} {
    gap: ${({ theme }) => theme.spacing(2.5)};
  }
`;

export default RestaurantOrdersPage;
